class CollectionSlider extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('.collection-slider__wrapper');
    this.slides = this.querySelectorAll('.collection-slider__slide');
    this.prevButton = this.querySelector('.collection-slider__button--prev');
    this.nextButton = this.querySelector('.collection-slider__button--next');

    this.startX = 0;
    this.scrollLeft = 0;
    this.isDragging = false;

    // Responsive settings
    this.slidesToScroll = window.innerWidth >= 990 ? 3 : window.innerWidth >= 768 ? 2 : 1;

    this.init();
  }

  init() {
    if (this.slides.length <= 0) return;

    // Set up navigation buttons
    this.prevButton.addEventListener('click', this.scrollPrev.bind(this));
    this.nextButton.addEventListener('click', this.scrollNext.bind(this));

    // Handle resize
    window.addEventListener('resize', this.handleResize.bind(this));

    // Initial button state
    this.updateButtonStates();
  }

  handleResize() {
    // Update slidesToScroll based on screen width
    this.slidesToScroll = window.innerWidth >= 990 ? 3 : window.innerWidth >= 768 ? 2 : 1;
  }

  scrollPrev() {
    // Find the first item that is fully visible
    const firstVisibleIndex = this.getFirstVisibleIndex();

    // Find the item that should become the last visible item after scrolling
    const targetIndex = Math.max(0, firstVisibleIndex - 1);

    // Scroll to the item that will make the target item the last visible one
    const visibleItemCount = this.getVisibleItemCount();
    const newFirstVisibleIndex = Math.max(0, targetIndex - visibleItemCount + 1);

    this.scrollToIndex(newFirstVisibleIndex);

    // Update button states after scrolling
    setTimeout(() => {
      this.updateButtonStates();
    }, 300);
  }

  getFirstVisibleIndex() {
    const sliderRect = this.slider.getBoundingClientRect();
    const sliderLeft = sliderRect.left;

    for (let i = 0; i < this.slides.length; i++) {
      const slideRect = this.slides[i].getBoundingClientRect();
      const slideRightEdge = slideRect.left + slideRect.width;

      // If this slide is at least partially visible (its right edge is past the slider's left edge)
      if (slideRightEdge > sliderLeft && slideRect.left >= sliderLeft - 5) {
        // 5px buffer
        return i;
      }
    }

    return 0;
  }

  scrollNext() {
    const lastVisibleIndex = this.getLastVisibleIndex();
    const targetIndex = lastVisibleIndex + 1;
    this.scrollToIndex(targetIndex);

    // Update button states after scrolling
    setTimeout(() => {
      this.updateButtonStates();
    }, 300);
  }

  getLastVisibleIndex() {
    const sliderRect = this.slider.getBoundingClientRect();
    let lastVisibleIndex = 0;

    for (let i = 0; i < this.slides.length; i++) {
      const slideRect = this.slides[i].getBoundingClientRect();
      const slideRightEdge = slideRect.left + slideRect.width;

      // If the right edge of the slide is visible in the viewport
      if (slideRightEdge <= sliderRect.right + 5) {
        // 5px buffer
        lastVisibleIndex = i;
      } else {
        break;
      }
    }

    return lastVisibleIndex;
  }

  scrollToIndex(index) {
    if (index < 0 || index >= this.slides.length) return;

    const targetSlide = this.slides[index];
    const slideWidth = targetSlide.offsetWidth;
    const slideMargin = parseInt(getComputedStyle(targetSlide).marginRight) || 0;

    this.slider.scrollTo({
      left: targetSlide.offsetLeft - this.slider.offsetLeft,
      behavior: 'smooth',
    });

    // Force an immediate update of the button states
    // This helps when we scroll to the first or last slide
    if (index === 0 || index === this.slides.length - 1) {
      this.updateButtonStates();
    }
  }

  updateButtonStates() {
    // First, check if we're at the start using the first visible index
    const firstVisibleIndex = this.getFirstVisibleIndex();
    const isAtStart = firstVisibleIndex === 0;

    // Check if we've reached the end (last slide is visible)
    const isAtEnd = this.isLastSlideVisible();

    // Set disabled state and visual appearance
    this.prevButton.disabled = isAtStart;
    this.nextButton.disabled = isAtEnd;

    this.prevButton.style.opacity = isAtStart ? '0.3' : '1';
    this.nextButton.style.opacity = isAtEnd ? '0.3' : '1';
  }

  isLastSlideVisible() {
    if (!this.slides.length) return true;

    const lastSlide = this.slides[this.slides.length - 1];
    const sliderRect = this.slider.getBoundingClientRect();
    const lastSlideRect = lastSlide.getBoundingClientRect();

    // Check if the right edge of the last slide is visible
    // We add a small buffer (5px) for slight calculation differences
    return lastSlideRect.right <= sliderRect.right + 5;
  }

  getVisibleItemCount() {
    // Calculate how many items are fully visible in the viewport
    const sliderWidth = this.slider.offsetWidth;
    const slideWidth = this.slides[0].offsetWidth;
    const slideMargin = parseInt(getComputedStyle(this.slides[0]).marginRight) || 0;

    // Calculate number of fully visible slides
    return Math.floor(sliderWidth / (slideWidth + slideMargin));
  }
}

customElements.define('collection-slider', CollectionSlider);
